// ── PurchaseLogPage — 메모 영수증 + 품목별 사용량 통계 ──
// 일반/외상 영수증 (purchaseLog) + 거래장부 lineItems를 통합해서 식재료 사용량 표시
// 매입금액 중복은 별도. 식재료 분석 전용 페이지.

import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, query, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../db';
import { useTransactions } from '../useTransactions';
import { aggregateItemUsage } from '../utils/itemUsageStats';
import { buildZhMap, zhOf } from '../i18n/BiName';   // V100 한중 병기
import { useLang } from '../i18n/LangContext';       // V108 매장 통화

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

const getCurrentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export default function PurchaseLogPage({ user, prices = [] }) {
  // V100 — 품목 중국어는 단가표가 유일한 출처. 여기선 이름으로 되찾아 병기만 한다.
  const zhMap = useMemo(() => buildZhMap(prices), [prices]);
  const { fmt: money, lang } = useLang();   // V108 매장 통화 · V147 lang
  const Z = (ko, zh) => (lang === 'zh' ? zh : ko);
  const [tab, setTab] = useState('items');  // 'items' | 'log'
  const [month, setMonth] = useState(getCurrentMonth());
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { transactions } = useTransactions(user, { limitCount: 1000 });

  useEffect(() => {
    if (!user?.uid) { setLoading(false); return; }
    const q = query(
      collection(db, 'users', user.uid, 'purchaseLog'),
      orderBy('createdAt', 'desc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error('purchaseLog 조회 실패:', err);
      setLoading(false);
    });
    return unsub;
  }, [user?.uid]);

  // 월별 필터링
  const monthLogs = useMemo(
    () => logs.filter(l => l.date && l.date.startsWith(month)),
    [logs, month]
  );

  // 품목별 통계 (transactions.lineItems + purchaseLog.items 통합)
  const items = useMemo(
    () => aggregateItemUsage(transactions, logs, { monthKey: month }),
    [transactions, logs, month]
  );
  const topItems = items.slice(0, 20);
  const maxAmount = topItems[0]?.totalAmount || 1;

  const totalLogAmount = monthLogs.reduce((s, l) => s + (Number(l.total) || 0), 0);

  const handleDelete = async (logId) => {
    if (!user?.uid) return;
    if (!window.confirm(Z('이 메모 영수증을 삭제할까요?', '要删除这张备注小票吗？'))) return;
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'purchaseLog', logId));
    } catch (e) {
      alert(Z(`삭제 실패: ${e.message}`, `删除失败：${e.message}`));
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>{Z('불러오는 중...', '加载中...')}</div>;
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#f0f2f5' }}>
      {/* sub-tab */}
      <div style={{
        display: 'flex', gap: 4, padding: '14px 20px 0',
        background: '#fff', borderBottom: '1px solid #e5e7eb',
        position: 'sticky', top: 0, zIndex: 30,
      }}>
        {[
          { id: 'items', label: Z('📦 품목별 사용량', '📦 各品项用量'), desc: '거래장부 + 메모 통합' },
          { id: 'log',   label: Z('📋 메모 영수증', '📋 备注小票'),    desc: '일반·외상 (거래장부 분리)' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '12px 18px', border: 'none', background: 'transparent',
            color: tab === t.id ? '#FF6F0F' : '#9ca3af',
            fontSize: 14, fontWeight: 700, cursor: 'pointer',
            borderBottom: tab === t.id ? '3px solid #FF6F0F' : '3px solid transparent',
          }}>{t.label}</button>
        ))}
      </div>

      <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
        <MonthBar month={month} onChange={setMonth} />

        {tab === 'items' && (
          <>
            <div style={{
              background: '#fff', borderRadius: 14, padding: '18px 22px',
              marginBottom: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#18181B' }}>
                {Z(`📦 이달 식재료·소모품 매입 ${items.length}종`, `📦 本月食材 · 消耗品进货 ${items.length} 种`)}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                {Z('총 매입가 ', '进货总额 ')}{money(items.reduce((s, i) => s + i.totalAmount, 0))} ·
                {Z('거래장부 lineItem + 메모 영수증 통합 (중복 없음)', '账本明细 + 备注小票合计（不重复）')}
              </div>
            </div>

            {items.length === 0 ? (
              <EmptyState
                icon="📦"
                title={Z('이달 품목별 데이터가 없습니다', '本月还没有品项数据')}
                desc={Z('영수증 OCR 또는 일반영수증을 메모 모드로 등록하면 품목별 사용량이 집계됩니다.', '用小票识别登记小票后，会按品项统计用量。')}
              />
            ) : (
              <div style={{
                background: '#fff', borderRadius: 12, padding: '16px 14px',
                boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
              }}>
                <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 14, color: '#374151' }}>
                  🏆 Top 20 {Z('식재료 · 소모품', '食材 · 消耗品')}
                </div>
                {topItems.map((item, idx) => (
                  <ItemBar key={item.key} item={item} idx={idx} maxAmount={maxAmount}
                           zh={zhOf(zhMap, item.displayName)} />
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'log' && (
          <>
            <div style={{
              background: '#fff', borderRadius: 14, padding: '18px 22px',
              marginBottom: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#EA580C' }}>
                {Z(`📋 이달 메모 영수증 ${monthLogs.length}건 · `, `📋 本月备注小票 ${monthLogs.length} 张 · `)}{money(totalLogAmount)}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                {Z('일반·외상영수증 — 거래장부 미입력 (손익·부가세 영향 X · 식재료 사용량만 집계)', '普通 · 赊账小票 — 不记入账本（只统计食材用量）')}
              </div>
            </div>

            {monthLogs.length === 0 ? (
              <EmptyState
                icon="📭"
                title={Z('이달 메모 영수증이 없습니다', '本月没有备注小票')}
                desc={Z("운영 → 매입 → 영수증 OCR에서 '📋 메모만' 모드로 저장하면 여기 쌓입니다.", '在 运营 → 进货 → 小票识别 里选「📋 只备注」保存后，会显示在这里。')}
              />
            ) : (
              <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden',
                            boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}>
                {monthLogs.map(log => (
                  <LogRow key={log.id} log={log} onDelete={() => handleDelete(log.id)} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function MonthBar({ month, onChange }) {
  const { lang } = useLang();   // V147
  const zh = lang === 'zh';
  const now = new Date();
  const months = [];
  // 24개월 — 옛 데이터 지원
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '10px 14px',
      marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
      boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
    }}>
      <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>📅 {zh ? '月份' : '월'}</span>
      <select value={month} onChange={e => onChange(e.target.value)} style={{
        padding: '6px 10px', borderRadius: 7,
        border: '1.5px solid #e5e7eb', fontSize: 13, background: '#fff', cursor: 'pointer',
      }}>
        {months.map(m => (
          <option key={m} value={m}>{zh ? `${m.slice(0, 4)}年${Number(m.slice(5))}月` : `${m.replace('-', '년 ')}월`}</option>
        ))}
      </select>
    </div>
  );
}

function ItemBar({ item, idx, maxAmount, zh }) {
  const { fmt: money, lang } = useLang();   // V108 매장 통화 · V147 lang
  const Z = (ko, zh) => (lang === 'zh' ? zh : ko);
  // 디자인 정책: 진행 막대 제거. 숫자만으로 비교 가능 (이미 1위가 100% = 기준).
  //   - 1위만 오렌지 액센트 (랭크 + 금액), 나머지는 다크/그레이
  const isFirst = idx === 0;
  return (
    <div style={{
      padding: '10px 0',
      borderBottom: '1px solid #F4F4F5',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{
            fontSize: 10, fontWeight: 800,
            color: isFirst ? '#fff' : '#52525B',
            background: isFirst ? '#FF6F0F' : '#F4F4F5',
            padding: '2px 7px', borderRadius: 4, flexShrink: 0,
            minWidth: 18, textAlign: 'center',
          }}>{idx + 1}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#18181B', minWidth: 0 }}>
            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.displayName}
            </span>
            {zh && (
              <span style={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#1A56A0',
                             marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {zh}
              </span>
            )}
          </span>
          {item.isTaxFree && (
            <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                          background: '#FAFAFA', color: '#71717A', flexShrink: 0 }}>{Z('면세', '免税')}</span>
          )}
        </div>
        <span style={{
          fontSize: 13, fontWeight: 800,
          color: isFirst ? '#FF6F0F' : '#18181B',
          flexShrink: 0,
        }}>
          {money(item.totalAmount)}
        </span>
      </div>
      <div style={{ fontSize: 11, color: '#71717A', marginTop: 4, marginLeft: 30 }}>
        {Z(`${item.count}회 매입 · 평균 단가 `, `进货 ${item.count} 次 · 平均单价 `)}{money(item.avgUnitPrice)}
        {item.minUnitPrice !== item.maxUnitPrice && (
          ` (${money(item.minUnitPrice)}~${money(item.maxUnitPrice)})`
        )}
        {item.sources.length > 0 && ` · ${item.sources.join('+')}`}
      </div>
    </div>
  );
}

function LogRow({ log, onDelete }) {
  const { fmt: money, lang } = useLang();   // V108 매장 통화 · V147 lang
  const Z = (ko, zh) => (lang === 'zh' ? zh : ko);
  return (
    <div style={{
      padding: '12px 16px', borderBottom: '1px solid #f0f0f0',
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>
            {log.date?.slice(5).replace('-', '/')}
          </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>
            {log.partnerName || Z('거래처 미상', '供应商不明')}
          </span>
        </div>
        {Array.isArray(log.items) && log.items.length > 0 && (
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {log.items.slice(0, 4).map(i => {
              const z = zhOf(zhMap, i.itemName);
              return z ? `${i.itemName} / ${z}` : i.itemName;
            }).filter(Boolean).join(', ')}
            {log.items.length > 4 && Z(` 외 ${log.items.length - 4}건`, ` 等 ${log.items.length} 项`)}
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#EA580C' }}>{money(log.total)}</div>
        <div style={{ fontSize: 10, color: '#9ca3af' }}>
          {log.items?.length || 0}{Z('품목', ' 项')}
          {log.taxFreeAmount > 0 && Z(` · 면세 ${fmt(log.taxFreeAmount)}`, ` · 免税 ${fmt(log.taxFreeAmount)}`)}
        </div>
      </div>
      <button onClick={onDelete} title={Z('삭제', '删除')} style={{
        width: 28, height: 28, borderRadius: '50%',
        border: '1px solid #F4F4F5', background: '#fff',
        color: '#52525B', cursor: 'pointer', fontSize: 13, flexShrink: 0,
      }}>×</button>
    </div>
  );
}

function EmptyState({ icon, title, desc }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '32px 20px',
      textAlign: 'center', color: '#9ca3af',
      boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
    }}>
      <div style={{ fontSize: 40, marginBottom: 10 }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>{desc}</div>
    </div>
  );
}
